import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as SendIcon } from 'assets/icons/send.svg';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import ContentLoader from 'components/ContentLoader/ContentLoader';
import EmptyList from 'components/EmptyList/EmptyList';
import EmptyTaskManagement from 'components/EmptyTaskManagement/EmptyTaskManagement';
import Select from 'components/Select/Select';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import TextField from 'components/TextField/TextField';
import { SELECTED_ITEMS } from 'constants/formFields';
import { MATERIAL_ASSESSMENT_RESPONDENT } from 'constants/materialAssessment';

import type IAnswerTypeOption from
  'pages/MaterialAssessmentPages/MaterialAssessmentSurveyCreation/components/CreateSurvey/interfaces/IAnswerTypeOption';
import type IRespondentRequest from 'store/slices/materialAssessment/interfaces/IRespondentRequest';
import type IRespondentTable from './interfaces/IRespondentTable';

import styles from './scss/RespondentTable.module.scss';

function RespondentTable({
  items,
  headCells,
  isLoadingMore,
  hasMore,
  onLoadMore,
  isInfoTable,
  handleChangeRespondentListKeyword,
  showHeaderCheckbox,
  respondentListKeyword,
  handleAddEmail,
  handleSendEmails,
  selectedRespondentItems,
  handleSelectRespondentItem,
  isLoadingRecipientList,
  disableActions,
}: IRespondentTable) {
  const { t } = useTranslation();
  const hyphen = t('common.hyphen');
  const [respondentTypesMap, setRespondentTypesMap] = useState<Record<string, string>>({});

  const respondentOptions = Object.keys(MATERIAL_ASSESSMENT_RESPONDENT).map((key) => ({
    label: t(`materialityAssessment.respondentTypes.${key}`, key),
    value: key,
  }));

  const emptyListText = (
    <Trans
      i18nKey="common.noResultsWithFilters"
      t={t}
      components={{
        br: <br />,
      }}
    />
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const throttleTimeout = useRef<NodeJS.Timeout | null>(null);
  const loaderTimeout = useRef<NodeJS.Timeout | null>(null);
  const keywordChangeTimeout = useRef<NodeJS.Timeout | null>(null);
  const prevKeywordRef = useRef<string>(respondentListKeyword);
  const [showLoader, setShowLoader] = useState(false);
  const [isKeywordChanging, setIsKeywordChanging] = useState(false);

  useEffect(() => {
    if (prevKeywordRef.current !== respondentListKeyword) {
      setIsKeywordChanging(true);
      prevKeywordRef.current = respondentListKeyword;

      if (keywordChangeTimeout.current) {
        clearTimeout(keywordChangeTimeout.current);
      }

      keywordChangeTimeout.current = setTimeout(() => {
        setIsKeywordChanging(false);
        keywordChangeTimeout.current = null;
      }, 300);
    }

    return () => {
      if (keywordChangeTimeout.current) {
        clearTimeout(keywordChangeTimeout.current);
        keywordChangeTimeout.current = null;
      }
    };
  }, [respondentListKeyword]);

  useEffect(() => {
    const isLoading = isLoadingRecipientList || isLoadingMore;

    if (isLoading || isKeywordChanging) {
      if (loaderTimeout.current) {
        clearTimeout(loaderTimeout.current);
        loaderTimeout.current = null;
      }
      setShowLoader(true);
    } else if (showLoader) {
      loaderTimeout.current = setTimeout(() => {
        setShowLoader(false);
        loaderTimeout.current = null;
      }, 500);
    }

    return () => {
      if (loaderTimeout.current) {
        clearTimeout(loaderTimeout.current);
        loaderTimeout.current = null;
      }
    };
  }, [isLoadingRecipientList, isLoadingMore, showLoader, isKeywordChanging]);

  useEffect(() => {
    const el = scrollRef.current;
    const handleScroll = () => {
      if (throttleTimeout.current) return;
      throttleTimeout.current = setTimeout(() => {
        if (!el || isLoadingMore || !hasMore) {
          throttleTimeout.current = null;
          return;
        }
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
          onLoadMore();
        }
        throttleTimeout.current = null;
      }, 150);
    };
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
        throttleTimeout.current = null;
      }
    };
  }, [isLoadingMore, hasMore, onLoadMore]);

  const handleUnSelectAll = () => {
    handleSelectRespondentItem(null);
  };

  const handleSelectItem = (item: IRespondentRequest) => {
    const selectedRespondent = selectedRespondentItems.find(
      (respondent) => respondent.email === item.email,
    );
    const respondentType = item.respondentType
      || selectedRespondent?.respondentType
      || respondentTypesMap[item.email]
      || '';
    const respondentWithType: IRespondentRequest = {
      ...item,
      respondentType,
    };
    handleSelectRespondentItem(respondentWithType);
  };

  const handleRespondentTypeChange = (email: string, selectedOption: unknown) => {
    const option = selectedOption as IAnswerTypeOption | null;
    const newType = option?.value || '';
    setRespondentTypesMap((prev) => ({
      ...prev,
      [email]: newType,
    }));

    const selectedRespondent = selectedRespondentItems.find(
      (respondent) => respondent.email === email,
    );
    if (selectedRespondent) {
      const updatedRespondent: IRespondentRequest = {
        ...selectedRespondent,
        respondentType: newType,
      };
      handleSelectRespondentItem(updatedRespondent);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.actionsWrapper}>
        <div className={styles.searchWrapper}>
          <TextField
            name="respondentListKeyword"
            value={respondentListKeyword}
            placeholder={t('common.searchByName')}
            icon={<SearchIcon />}
            onChange={(event) => handleChangeRespondentListKeyword(event.target.value)}
          />
        </div>
        <div className={styles.buttonsWrapper}>
          <Button
            title={t('materialityAssessment.addEmailButton')}
            startIcon={<PlusIcon />}
            variant="ghost"
            onClick={handleAddEmail}
            className={styles.downloadButton}
            disabled={disableActions}
          />
          <Button
            title={t('materialityAssessment.sendEmailsButton')}
            startIcon={<SendIcon />}
            onClick={handleSendEmails}
            className={styles.button}
            disabled={
              disableActions
              || selectedRespondentItems.length === 0
              || selectedRespondentItems.some(
                (respondent) => !respondent.respondentType || respondent.respondentType.trim() === '',
              )
            }
          />
        </div>
      </div>
      {showLoader ? (
        <ContentLoader title={t('materialityAssessment.loadingRecipientList')} />
      ) : null}

      {(!showLoader && items && items.length === 0 && !respondentListKeyword) ? (
        <EmptyTaskManagement
          title={t('materialityAssessment.emptyRespondentTable')}
          buttonTitle={t('materialityAssessment.addEmailButton')}
          buttonOnClick={handleAddEmail}
          checkIcon
          disabledButton={disableActions}
        />
      ) : null}

      {(!showLoader && items && items.length === 0 && respondentListKeyword) ? (
        <EmptyList text={emptyListText} />
      ) : null}

      {(!showLoader && items && items.length > 0) ? (
        <div
          className={styles.tableBodyScroll}
          ref={scrollRef}
          aria-label="Analysis table body"
          role="region"
        >
          <Table
            className={clsx(styles.table, {
              [styles.tableInfoTable]: isInfoTable,
            })}
          >
            <TableHead className={clsx(styles.header, {
              [styles.headerInfoTable]: isInfoTable,
            })}
            >
              {headCells.map(({
                id, label,
              }, index) => (
                <TableCell head className={styles.tableCell} key={id}>
                  <div>
                    <span className={styles.tableCellLabel}>
                      {showHeaderCheckbox && index === 0 ? (
                        <div className={styles.checkboxLabel}>
                          <div className={styles.checkboxColumn}>
                            <Checkbox
                              name="vs-all"
                              className={clsx(
                                styles.checkbox,
                                [selectedRespondentItems.length === 0 && styles.disabled],
                              )}
                              checked={selectedRespondentItems.length > 0}
                              value={String(selectedRespondentItems.length > 0)}
                              onChange={handleUnSelectAll}
                              label={false}
                              aria-label={t('common.select')}
                              disabled={selectedRespondentItems.length === 0}
                              isLineMark
                            />
                          </div>
                          {t(label)}
                        </div>
                      ) : t(label)}
                    </span>
                  </div>
                </TableCell>
              ))}
            </TableHead>
            <TableBody className={styles.body}>
              {items ? items?.map((item, index) => (
                <TableRow key={[item.id, index].join('_')}>
                  <TableCell>
                    <div className={styles.staticCell}>
                      <div className={styles.checkboxColumn}>
                        <Checkbox
                          key={[item.email, item.name].join('_')}
                          name={`${SELECTED_ITEMS}-${item.email}`}
                          checked={selectedRespondentItems.some((selectedItem) => selectedItem.email === item.email)}
                          value={item.email}
                          onChange={() => handleSelectItem(item)}
                          label={false}
                          isLineMark={false}
                        />
                      </div>
                      <span>
                        {item.name || hyphen}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className={styles.emailCell}>
                      {item.email || hyphen}
                    </div>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const selectedRespondent = selectedRespondentItems.find(
                        (respondent) => respondent.email === item.email,
                      );
                      const typeFromServer = item.respondentType;
                      const typeFromSelector = selectedRespondent?.respondentType
                        || respondentTypesMap[item.email];

                      if (typeFromServer) {
                        return t(`materialityAssessment.${typeFromServer}`, typeFromServer);
                      }

                      return (
                        <Select
                          name={`respondentType-${item.email}`}
                          value={respondentOptions.find(
                            (option) => option.value === typeFromSelector,
                          )}
                          options={respondentOptions}
                          placeholder={t('materialityAssessment.respondentRole')}
                          onChange={(selectedOption: unknown) => {
                            handleRespondentTypeChange(item.email, selectedOption);
                          }}
                          formatOptionLabel={(option: IAnswerTypeOption) => (
                            <div>
                              <span>
                                {t(
                                  `materialityAssessment.${option.value}`,
                                  option.value,
                                )}
                              </span>
                            </div>
                          )}
                          menuPosition="absolute"
                          menuPortalTarget={document.body}
                          modalRef={scrollRef}
                          className={styles.respondentTypeSelect}
                        />
                      );
                    })()}
                  </TableCell>
                </TableRow>
              )) : null}
            </TableBody>
          </Table>
        </div>
      ) : null}
    </div>
  );
}

export default RespondentTable;
